import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "photos", "camera", "previews", "messages", "history", "form", "send", "uploadStatus", "older", "microphone", "speechStatus", "generationKind", "imageMode", "imageBadge", "searchMode", "searchBadge", "browserModes", "browserMode", "browserBadge", "addMenu", "addToggle", "voiceMode"]
  static values = { userId: String }

  connect() {
    this.urls = []
    this.uploads = []
    this.interacted = false
    this.followBottom = true
    this.desktopHistory = window.matchMedia("(min-width: 1024px)")
    this.historyLayoutChanged = () => {
      this.closeSidebar()
      this.updateHistoryToggle()
    }
    this.desktopHistory.addEventListener("change", this.historyLayoutChanged)
    this.updateHistoryToggle()
    this.resize()
    this.restoreScroll()
    this.contentObserver = new ResizeObserver(() => this.scheduleBottom())
    this.contentObserver.observe(this.messagesTarget)
    Array.from(this.messagesTarget.children).forEach((child) => this.contentObserver.observe(child))
    document.fonts?.ready.then(() => { if (this.element.isConnected) this.restoreScroll() })
    if (this.hasInputTarget && this.voiceResume()) {
      this.setVoiceResume(false)
      this.voiceActive = true
      this.updateVoiceButtons()
      this.expectedVoiceReply = this.element.querySelector("[data-ai-status-url-value]")?.dataset.aiStatusUrlValue
      if (this.inputTarget.disabled) this.speechStatusTarget.textContent = "Голосовой диалог: жду ответ. Микрофон — остановить."
      else {
        this.expectedVoiceReply = this.element.querySelector(".chat-turn:last-child")?.dataset.aiRequestUrl
        if (this.expectedVoiceReply) this.readVoiceReply()
        else this.stopVoice("Не удалось отправить вопрос. Проверьте сообщение в чате.")
      }
    }
  }

  disconnect() {
    this.browserStatusAbort?.abort()
    this.closeSidebar()
    this.desktopHistory?.removeEventListener("change", this.historyLayoutChanged)
    this.voiceActive = false
    window.clearTimeout(this.voiceTimer)
    if (this.voiceSpeech) window.speechSynthesis?.cancel()
    this.recognition?.abort()
    this.historyAbort?.abort()
    this.urls.forEach((url) => URL.revokeObjectURL(url))
    this.uploads.forEach((upload) => upload.abort?.abort())
    this.contentObserver?.disconnect()
    window.cancelAnimationFrame(this.scrollFrame)
  }

  restoreScroll() {
    if (this.interacted) return
    this.followBottom = true
    this.scheduleBottom()
  }

  scheduleBottom() {
    window.cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = window.requestAnimationFrame(() => {
        if (this.element.isConnected && this.followBottom) this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      })
    })
  }

  markInteraction() {
    this.interacted = true
    this.readingAnswer = false
  }

  answerReceived(event) {
    const turn = Array.from(this.messagesTarget.querySelectorAll(".chat-turn")).find((node) => node.dataset.aiRequestUrl === event.detail.url)
    const answer = turn?.querySelector(".chat-assistant-message")
    if (!answer) return
    // A completed answer starts a reading position, not bottom-following.
    this.interacted = true
    this.readingAnswer = true
    this.followBottom = false
    window.cancelAnimationFrame(this.scrollFrame)
    const align = () => {
      if (!answer.isConnected || !this.element.isConnected) return
      const messages = this.messagesTarget
      messages.scrollTop += answer.getBoundingClientRect().top - messages.getBoundingClientRect().top - 16
    }
    align()
    this.scrollFrame = window.requestAnimationFrame(align)
  }

  trackScroll() {
    if (!this.interacted || this.readingAnswer) return
    const messages = this.messagesTarget
    this.followBottom = messages.scrollHeight - messages.scrollTop - messages.clientHeight < 96
    if (messages.scrollTop < 120 && this.hasOlderTarget) this.loadOlder()
  }
  toggleSidebar() {
    if (this.desktopHistory.matches) {
      this.element.classList.toggle("desktop-history-hidden")
      this.updateHistoryToggle()
      return
    }
    if (this.element.classList.contains("sidebar-open")) return this.closeSidebar()
    this.historyFocus = document.activeElement
    this.historyOverflow = document.body.style.overflow
    this.element.classList.add("sidebar-open")
    document.body.style.overflow = "hidden"
    this.historyTarget.setAttribute("role", "dialog")
    this.historyTarget.setAttribute("aria-modal", "true")
    this.historyTarget.setAttribute("aria-labelledby", "chat-history-title")
    document.querySelector('[aria-controls="chat-sidebar"]')?.setAttribute("aria-expanded", "true")
    this.historyTarget.querySelector("button")?.focus()
  }
  closeSidebar(event) {
    this.closeAddMenu(event)
    if (this.desktopHistory?.matches && event?.type === "click") {
      this.element.classList.add("desktop-history-hidden")
      this.updateHistoryToggle()
    }
    if (!this.element.classList.contains("sidebar-open")) return
    this.element.classList.remove("sidebar-open")
    document.body.style.overflow = this.historyOverflow || ""
    this.historyTarget.removeAttribute("role")
    this.historyTarget.removeAttribute("aria-modal")
    this.historyTarget.removeAttribute("aria-labelledby")
    this.updateHistoryToggle()
    if (event?.type === "click" || event?.key === "Escape") this.historyFocus?.focus()
  }

  updateHistoryToggle() {
    const open = this.desktopHistory.matches ? !this.element.classList.contains("desktop-history-hidden") : this.element.classList.contains("sidebar-open")
    document.querySelector('[aria-controls="chat-sidebar"]')?.setAttribute("aria-expanded", String(open))
  }

  trapHistory(event) {
    if (event.key !== "Tab" || !this.element.classList.contains("sidebar-open")) return
    const items = Array.from(this.historyTarget.querySelectorAll("a[href], button:not([disabled]), select:not([disabled]), summary")).filter((item) => item.getClientRects().length)
    const first = items[0], last = items[items.length - 1]
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
  }

  openLimits() {
    this.closeSidebar()
    document.dispatchEvent(new Event("navigation:limits"))
  }

  toggleImageMode() {
    this.closeAddMenu()
    if (this.imageModeTarget.dataset.quotaExhausted === "true") return this.openLimits()
    const image = this.generationKindTarget.value !== "image"
    this.generationKindTarget.value = image ? "image" : "chat"
    this.resetBrowserSelections()
    if (this.hasSearchModeTarget) this.searchModeTarget.setAttribute("aria-pressed", "false")
    if (this.hasSearchBadgeTarget) this.searchBadgeTarget.hidden = true
    this.imageModeTarget.setAttribute("aria-pressed", String(image))
    if (this.hasImageBadgeTarget) this.imageBadgeTarget.hidden = !image
    this.inputTarget.placeholder = image ? "Что нарисовать или изменить?" : "Сообщение…"
    this.inputTarget.focus()
  }

  toggleSearchMode() {
    this.closeAddMenu()
    const search = this.generationKindTarget.value !== "search"
    this.generationKindTarget.value = search ? "search" : "chat"
    this.resetBrowserSelections()
    this.searchModeTarget.setAttribute("aria-pressed", String(search))
    this.searchBadgeTarget.hidden = !search
    if (this.hasImageModeTarget) this.imageModeTarget.setAttribute("aria-pressed", "false")
    if (this.hasImageBadgeTarget) this.imageBadgeTarget.hidden = true
    this.inputTarget.placeholder = search ? "Что найти в интернете?" : "Сообщение…"
    this.inputTarget.focus()
  }

  resetBrowserSelections() {
    this.browserModeTargets.forEach((button) => button.setAttribute("aria-pressed", "false"))
    if (this.hasBrowserBadgeTarget) this.browserBadgeTarget.hidden = true
  }

  toggleBrowserMode(event) {
    const engine = event.currentTarget.dataset.engine
    if (!["google", "yandex", "both"].includes(engine)) return
    const kind = `browser_${engine}`
    if (this.generationKindTarget.value === kind) return this.clearBrowserMode()
    this.closeAddMenu()
    this.resetBrowserSelections()
    this.generationKindTarget.value = kind
    event.currentTarget.setAttribute("aria-pressed", "true")
    this.browserBadgeTarget.textContent = `${({ google: "Google", yandex: "Яндекс", both: "Яндекс + Google" })[engine]} · Chrome ×`
    this.browserBadgeTarget.hidden = false
    if (this.hasSearchModeTarget) this.searchModeTarget.setAttribute("aria-pressed", "false")
    if (this.hasSearchBadgeTarget) this.searchBadgeTarget.hidden = true
    if (this.hasImageModeTarget) this.imageModeTarget.setAttribute("aria-pressed", "false")
    if (this.hasImageBadgeTarget) this.imageBadgeTarget.hidden = true
    this.inputTarget.placeholder = "Что найти в Chrome на отдельном ПК?"
    this.inputTarget.focus()
  }

  clearBrowserMode() {
    this.closeAddMenu()
    this.resetBrowserSelections()
    this.generationKindTarget.value = "chat"
    this.inputTarget.placeholder = "Сообщение…"
    this.inputTarget.focus()
  }

  async refreshBrowserModes() {
    if (!this.hasBrowserModesTarget) return
    this.browserModesTarget.hidden = true
    this.browserStatusAbort?.abort()
    const controller = new AbortController()
    this.browserStatusAbort = controller
    let available = false
    let engines = []
    try {
      const response = await fetch(this.browserModesTarget.dataset.url, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal })
      if (response.ok) {
        const data = await response.json()
        available = data.available === true
        engines = Array.isArray(data.engines) ? data.engines : []
      }
    } catch { /* An unavailable machine never leaves selectable browser modes. */ }
    if (!this.element.isConnected || controller.signal.aborted) return
    this.browserModesTarget.hidden = !available
    this.browserModeTargets.forEach((button) => { button.hidden = !engines.includes(button.dataset.engine) })
    const kind = this.generationKindTarget.value
    if (kind.startsWith("browser_") && (!available || !engines.includes(kind.slice(8)))) {
      this.clearBrowserMode()
      this.speechStatusTarget.textContent = "Браузерный поиск недоступен или разрешение отозвано. Режим выключен; черновик сохранён."
    }
  }

  toggleAddMenu() {
    const open = this.addMenuTarget.hidden
    this.addMenuTarget.hidden = !open
    this.addToggleTarget.setAttribute("aria-expanded", String(open))
    if (open) this.refreshBrowserModes()
    if (open) this.addMenuTarget.querySelector("button:not([disabled])")?.focus()
  }

  closeAddMenu(event) {
    if (!this.hasAddMenuTarget) return
    const wasOpen = !this.addMenuTarget.hidden
    this.addMenuTarget.hidden = true
    this.addToggleTarget.setAttribute("aria-expanded", "false")
    if (wasOpen && event?.key === "Escape") this.addToggleTarget.focus()
  }

  outsideMenu(event) {
    if (!this.hasAddMenuTarget || this.addMenuTarget.hidden) return
    if (!this.addMenuTarget.contains(event.target) && !this.addToggleTarget.contains(event.target)) this.closeAddMenu()
  }

  choosePhotos() {
    if (this.voiceActive) this.stopVoice()
    this.photosTarget.click()
    this.closeAddMenu()
  }

  chooseCamera() {
    if (this.voiceActive) this.stopVoice()
    this.cameraTarget.click()
    this.closeAddMenu()
  }

  voiceResume() {
    try { return sessionStorage.getItem(`parfino.voice.${this.userIdValue}`) === "resume" } catch { return false }
  }

  setVoiceResume(resume) {
    try {
      const key = `parfino.voice.${this.userIdValue}`
      if (resume) sessionStorage.setItem(key, "resume")
      else sessionStorage.removeItem(key)
    } catch { /* Voice still works without navigation persistence. */ }
  }

  updateVoiceButtons() {
    if (!this.hasMicrophoneTarget) return
    this.microphoneTarget.setAttribute("aria-label", this.voiceActive ? "Остановить голосовой диалог" : "Надиктовать сообщение")
    this.microphoneTarget.setAttribute("aria-pressed", String(Boolean(this.voiceActive || this.listening)))
    this.microphoneTarget.disabled = this.inputTarget.disabled && !this.voiceActive
    this.microphoneTarget.querySelector("i").className = this.voiceActive ? "bi bi-stop-circle" : "bi bi-mic"
    if (this.hasVoiceModeTarget) this.voiceModeTarget.setAttribute("aria-pressed", String(Boolean(this.voiceActive)))
  }

  toggleVoiceMode() {
    this.closeAddMenu()
    if (this.voiceActive) return this.stopVoice()
    if (!(window.SpeechRecognition || window.webkitSpeechRecognition) || !window.speechSynthesis || !window.SpeechSynthesisUtterance) {
      this.speechStatusTarget.textContent = "Голосовой диалог недоступен в этом браузере. Можно использовать диктовку или голосовую клавиатуру телефона."
      return
    }
    if (this.inputTarget.value.trim() || this.uploads.length) {
      this.speechStatusTarget.textContent = "Сначала отправьте или очистите черновик и вложения. Голосовой диалог отправляет вопросы автоматически."
      return
    }
    if (this.listening) {
      this.speechStatusTarget.textContent = "Сначала остановите диктовку нажатием на микрофон."
      return
    }
    this.voiceActive = true
    this.updateVoiceButtons()
    this.startRecognition(true)
  }

  stopVoice(message = "Голосовой диалог выключен.") {
    this.voiceActive = false
    this.setVoiceResume(false)
    window.clearTimeout(this.voiceTimer)
    const recognition = this.recognition
    this.recognition = null
    this.listening = false
    recognition?.abort()
    if (this.voiceSpeech) window.speechSynthesis?.cancel()
    this.updateVoiceButtons()
    if (this.hasSpeechStatusTarget && this.element.isConnected) this.speechStatusTarget.textContent = message
  }

  beforeVisit(event) {
    const path = new URL(event.detail.url, window.location.origin).pathname
    if (this.voiceActive && (!this.awaitingVoiceReply || !/^\/(ai(?:\/|$)|chats(?:\/|$))/.test(path))) this.stopVoice()
  }

  visibilityChanged() {
    if (!document.hidden) return
    if (this.voiceActive) this.stopVoice("Голосовой диалог остановлен: вкладка скрыта.")
    else this.recognition?.abort()
  }

  async readVoiceReply() {
    const url = this.expectedVoiceReply
    try {
      const response = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("Unavailable")
      const data = await response.json()
      if (this.element.isConnected) this.voiceReply({ detail: { url, ...data } })
    } catch { if (this.voiceActive && this.element.isConnected) this.stopVoice("Не удалось получить ответ для озвучки.") }
  }

  voiceReply(event) {
    if (!this.voiceActive || event.detail.url !== this.expectedVoiceReply) return
    this.expectedVoiceReply = null
    if (event.detail.status !== "completed") return this.stopVoice("Не удалось получить ответ. Голосовой диалог остановлен.")
    const text = String(event.detail.answer || "Ответ готов.").replace(/```[\s\S]*?```/g, "Пример кода доступен в чате.").replace(/https?:\/\/\S+/g, "ссылка в чате").replace(/[#*_`]/g, "").slice(0, 3000)
    const speech = new SpeechSynthesisUtterance(text || "Ответ готов.")
    this.voiceSpeech = speech
    speech.lang = "ru-RU"
    const voices = window.speechSynthesis.getVoices().filter((item) => item.lang.startsWith("ru"))
    const voice = voices.find((item) => item.localService) || voices[0]
    if (voice) speech.voice = voice
    this.speechStatusTarget.textContent = "Озвучиваю ответ… Микрофон — остановить голосовой диалог."
    speech.onend = () => {
      this.voiceSpeech = null
      if (this.voiceActive && this.element.isConnected) this.voiceTimer = window.setTimeout(() => this.startRecognition(true), 300)
    }
    speech.onerror = () => { if (this.voiceActive) this.stopVoice("Браузер не смог озвучить ответ. Текст сохранён в чате.") }
    window.speechSynthesis.speak(speech)
  }

  async loadOlder() {
    if (this.loadingOlder || !this.hasOlderTarget) return
    this.loadingOlder = true
    const button = this.olderTarget
    button.disabled = true
    button.textContent = "Загрузка…"
    this.historyAbort = new AbortController()
    try {
      const url = new URL(button.dataset.url, window.location.origin)
      url.searchParams.set("before", button.dataset.before)
      const response = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" }, signal: this.historyAbort.signal })
      if (!response.ok) throw new Error("Unavailable")
      const data = await response.json()
      if (!this.element.isConnected) return
      const messages = this.messagesTarget
      const height = messages.scrollHeight
      const top = messages.scrollTop
      this.interacted = true
      this.followBottom = false
      button.insertAdjacentHTML("afterend", data.html)
      if (data.more) button.dataset.before = data.before
      else button.remove()
      messages.scrollTop = top + messages.scrollHeight - height
      Array.from(messages.children).forEach((child) => this.contentObserver.observe(child))
    } catch {
      button.textContent = "Не удалось загрузить. Повторить"
    } finally {
      this.loadingOlder = false
      button.disabled = false
      if (button.textContent === "Загрузка…") button.textContent = "Более ранние сообщения"
    }
  }

  dictate() {
    this.closeAddMenu()
    if (this.voiceActive) return this.stopVoice()
    if (this.listening) { this.recognition?.stop(); return }
    this.startRecognition(false)
  }

  startRecognition(automatic) {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Recognition) {
      const message = "Браузер не поддерживает диктовку. Попробуйте Chrome или голосовой ввод клавиатуры телефона."
      if (automatic) this.stopVoice(message)
      else this.speechStatusTarget.textContent = message
      return
    }
    const recognition = new Recognition()
    this.recognition = recognition
    recognition.lang = "ru-RU"
    recognition.continuous = !automatic
    recognition.interimResults = false
    let heard = false
    let failed = false
    recognition.onstart = () => {
      if (this.recognition !== recognition || !this.element.isConnected) return
      this.listening = true
      this.updateVoiceButtons()
      this.speechStatusTarget.textContent = automatic ? "Слушаю… После паузы вопрос отправится автоматически. Микрофон — выйти из голосового диалога." : "Слушаю… Надиктуйте текст, нажмите микрофон для остановки, затем отправьте сообщение кнопкой со стрелкой."
    }
    recognition.onresult = (event) => {
      if (this.recognition !== recognition || !this.element.isConnected) return
      const text = Array.from(event.results).slice(event.resultIndex).filter((result) => result.isFinal).map((result) => result[0].transcript).join(" ")
      heard ||= Boolean(text.trim())
      this.inputTarget.value = [this.inputTarget.value.trim(), text].filter(Boolean).join(" ").slice(0, 8000)
      this.resize()
    }
    recognition.onerror = (event) => {
      if (this.recognition !== recognition) return
      if (event.error === "aborted") return
      failed = true
      const message = event.error === "not-allowed" ? "Доступ к микрофону запрещён. Разрешите его в настройках браузера." : "Не удалось распознать речь. Попробуйте ещё раз."
      if (automatic) this.stopVoice(message)
      else if (this.element.isConnected) this.speechStatusTarget.textContent = message
    }
    recognition.onend = () => {
      if (this.recognition !== recognition) return
      this.listening = false
      if (!this.element.isConnected) return
      this.updateVoiceButtons()
      if (automatic) {
        if (this.voiceActive && !failed && heard && this.inputTarget.value.trim() && !this.sendTarget.disabled && !this.awaitingVoiceReply) this.formTarget.requestSubmit()
        else if (this.voiceActive && !this.awaitingVoiceReply) this.stopVoice("Речь не распознана. Нажмите «Голосовой диалог», чтобы попробовать ещё раз.")
        return
      }
      if (this.speechStatusTarget.textContent.startsWith("Слушаю")) this.speechStatusTarget.textContent = "Диктовка завершена. Проверьте текст перед отправкой."
    }
    try { recognition.start() }
    catch { if (automatic) this.stopVoice("Не удалось включить микрофон."); else this.speechStatusTarget.textContent = "Не удалось включить микрофон." }
  }

  resize() {
    if (!this.hasInputTarget) return
    this.inputTarget.style.height = "auto"
    this.inputTarget.style.height = `${Math.min(this.inputTarget.scrollHeight, 180)}px`
  }

  sendOnEnter(event) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing && !this.inputTarget.disabled) {
      event.preventDefault()
      if (!this.sendTarget.disabled && (this.inputTarget.value.trim() || this.uploads.length)) this.formTarget.requestSubmit()
    }
  }

  paste(event) {
    const images = Array.from(event.clipboardData?.files || []).filter((file) => file.type.startsWith("image/"))
    if (!images.length) return
    event.preventDefault()
    this.addPhotos(images)
  }

  preview(event) {
    const field = event?.target || this.photosTarget
    this.addPhotos(Array.from(field.files))
    field.value = ""
  }

  guardSubmit(event) {
    if (navigator.onLine === false || this.sendTarget.dataset.connectionUnavailable === "true") {
      event.preventDefault()
      this.connectionLost("Нет соединения с сайтом. Сообщение не отправлено; текст и фото сохранены на этой странице. Проверяем восстановление связи…")
      return
    }
    this.awaitingVoiceReply = Boolean(this.voiceActive)
    this.setVoiceResume(this.awaitingVoiceReply)
    if (this.listening) this.recognition?.stop()
    if (this.uploads.some((upload) => !upload.id)) {
      event.preventDefault()
      this.setVoiceResume(false)
      this.uploadStatusTarget.textContent = "Дождитесь загрузки или удалите фото с ошибкой."
    }
  }

  submissionFinished(event) {
    if (!event.detail.success && this.voiceActive) this.stopVoice("Вопрос не отправлен. Проверьте соединение и сообщение в чате.")
    if (event.detail.success) return
    const status = event.detail.fetchResponse?.statusCode
    if (event.detail.error || !event.detail.fetchResponse || status >= 500) {
      this.connectionLost("Нет соединения с сайтом или ИИ. Текст и фото остаются на этой странице. Проверяем связь. Если отправка уже началась, проверьте чат перед повторной отправкой.", status >= 500)
    }
  }

  connectionLost(message, checkAI = false) {
    document.dispatchEvent(new CustomEvent("chat:connection-lost", { detail: { message, checkAI } }))
  }

  refreshUploads() {
    if (!this.element.isConnected) return
    const waiting = this.uploads.some((upload) => !upload.id)
    this.sendTarget.disabled = waiting || this.inputTarget.disabled || this.sendTarget.dataset.connectionUnavailable === "true"
    this.uploadStatusTarget.textContent = waiting ? this.uploads.some((upload) => upload.error) ? "Не все фото загружены. Нажмите «Повторить» возле фото или удалите его." : "Загрузка фото…" : this.uploads.length ? "Фото загружены" : ""
    document.dispatchEvent(new CustomEvent("chat:uploads-changed"))
  }

  async removeUpload(id) {
    if (!id) return
    await fetch(`/ai_uploads/${id}`, { method: "DELETE", credentials: "same-origin", headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" } }).catch(() => {})
  }

  addPhotos(files) {
    if (this.uploads.length + files.length > 10) {
      this.uploadStatusTarget.textContent = "Можно прикрепить не более десяти фотографий."
      return
    }
    files.forEach((file) => {
      const item = document.createElement("div")
      item.className = "chat-preview"
      const upload = { item, id: null, removed: false }
      this.uploads.push(upload)
      const image = document.createElement("img")
      image.src = URL.createObjectURL(file)
      this.urls.push(image.src)
      image.alt = file.name
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "chat-preview-remove"
      remove.textContent = "×"
      remove.setAttribute("aria-label", `Убрать фото ${this.uploads.length}`)
      remove.addEventListener("click", () => {
        upload.removed = true
        upload.abort?.abort()
        this.uploads = this.uploads.filter((entry) => entry !== upload)
        item.remove()
        this.removeUpload(upload.id)
        this.refreshUploads()
      })
      item.append(image, remove)
      this.previewsTarget.append(item)
      this.uploadPhoto(upload, file)
    })
    this.refreshUploads()
  }

  async uploadPhoto(upload, file) {
    upload.status?.remove()
    upload.retry?.remove()
    upload.error = false
    const status = document.createElement("span")
    upload.status = status
    status.className = "small"
    status.textContent = "Загрузка…"
    upload.item.append(status)
    this.refreshUploads()
    upload.abort = new AbortController()
    const timeout = window.setTimeout(() => upload.abort.abort(), 30000)
    try {
      if (file.size > 4 * 1024 * 1024) throw new Error("Фото больше 4 МБ")
      const data = new FormData()
      data.append("photo", file)
      const response = await fetch("/ai_uploads", { method: "POST", body: data, credentials: "same-origin", signal: upload.abort.signal, headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" } })
      if (response.status >= 500) throw new TypeError("Connection unavailable")
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || "Ошибка загрузки")
      upload.id = result.id
      if (upload.removed || !this.element.isConnected) { await this.removeUpload(upload.id); return }
      const field = document.createElement("input")
      field.type = "hidden"
      field.name = "ai_request[upload_ids][]"
      field.value = upload.id
      upload.item.append(field)
      status.textContent = "Готово"
    } catch (error) {
      upload.error = true
      const connectionError = error instanceof TypeError || error instanceof SyntaxError || error.name === "AbortError"
      status.textContent = connectionError ? "Нет соединения. Фото не загружено." : error.message || "Ошибка загрузки"
      if (connectionError && !upload.removed && this.element.isConnected) {
        this.connectionLost("Нет соединения с сайтом. Фото не удалось загрузить; оно осталось на этой странице. После восстановления связи нажмите «Повторить» возле фото.")
        const retry = document.createElement("button")
        retry.type = "button"
        retry.className = "btn btn-sm btn-outline-primary"
        retry.textContent = "Повторить"
        retry.setAttribute("aria-label", "Повторить загрузку фото")
        retry.addEventListener("click", () => this.uploadPhoto(upload, file))
        upload.retry = retry
        upload.item.append(retry)
      }
    } finally {
      window.clearTimeout(timeout)
      this.refreshUploads()
    }
  }
}
